import { useMemo } from 'react';
import { useIntl } from 'umi';

/** What every Carat field renders as when the export did not carry it. */
export const CARAT_EMPTY = '—';

const DATE_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/**
 * Carat dates arrive as plain `Y-m-d[ H:i:s]` strings without a zone: they are
 * wall-clock times from the studio's own machine. Parsing them as local instead
 * of letting `new Date()` read them as UTC keeps a quotation dated the day it
 * was written.
 */
const toDate = (value?: string | null): Date | null => {
  const match = value ? DATE_PATTERN.exec(value.trim()) : null;
  if (!match) return null;

  const [, year, month, day, hour, minute, second] = match;

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour ?? 0),
    Number(minute ?? 0),
    Number(second ?? 0),
  );
};

export interface CaratFormatter {
  /** A price in the quotation's currency. */
  money: (value?: number | null) => string;
  /** Same as `money`, but always carrying its sign — for discounts and credits. */
  signedMoney: (value?: number | null) => string;
  number: (value?: number | null, maximumFractionDigits?: number) => string;
  percent: (value?: number | null) => string;
  date: (value?: string | null) => string;
  dateTime: (value?: string | null) => string;
  /** `600 × 561 × 720 mm`, skipping the axes the export left at zero. */
  dimensions: (
    value?: {
      width?: number | null;
      depth?: number | null;
      height?: number | null;
    } | null,
  ) => string;
  /** Anything that is either a value or nothing to show. */
  text: (value?: string | null) => string;
}

/**
 * Formatting for the Carat planning viewer. Carat is a German desktop product,
 * but the numbers it exports are plain decimals — they are rendered in the back
 * office user's own locale, in the currency the quotation was written in.
 */
export const useCaratFormat = (currency?: string | null): CaratFormatter => {
  const intl = useIntl();
  const locale = intl.locale;

  return useMemo(() => {
    const money = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'EUR',
    });

    const formatMoney = (value?: number | null) =>
      value === null || value === undefined ? CARAT_EMPTY : money.format(value);

    return {
      money: formatMoney,
      signedMoney: (value) =>
        value === null || value === undefined || value === 0
          ? formatMoney(value)
          : `${value > 0 ? '+' : ''}${money.format(value)}`,
      number: (value, maximumFractionDigits = 2) =>
        value === null || value === undefined
          ? CARAT_EMPTY
          : new Intl.NumberFormat(locale, { maximumFractionDigits }).format(
              value,
            ),
      percent: (value) =>
        value === null || value === undefined
          ? CARAT_EMPTY
          : `${new Intl.NumberFormat(locale, {
              maximumFractionDigits: 2,
            }).format(value)} %`,
      date: (value) => {
        const date = toDate(value);

        return date ? intl.formatDate(date) : CARAT_EMPTY;
      },
      dateTime: (value) => {
        const date = toDate(value);

        return date
          ? `${intl.formatDate(date)} ${intl.formatTime(date)}`
          : CARAT_EMPTY;
      },
      dimensions: (value) => {
        const axes = [value?.width, value?.depth, value?.height].filter(
          (axis): axis is number => typeof axis === 'number' && axis > 0,
        );

        return axes.length === 0
          ? CARAT_EMPTY
          : `${axes
              .map((axis) => new Intl.NumberFormat(locale).format(axis))
              .join(' × ')} mm`;
      },
      text: (value) => (value ? value : CARAT_EMPTY),
    };
  }, [intl, locale, currency]);
};

/**
 * Carat files every position under a trade group. Only the colour is derived
 * from the code — the label itself always comes from the export, which writes it
 * in the studio's own wording.
 */
export const CARAT_TRADE_GROUP_COLORS: Record<string, string> = {
  H: 'gold',
  K: 'gold',
  E: 'blue',
  G: 'blue',
  Z: 'green',
};
